import * as _ from "lodash"
import { IHaveExecutionBounds } from "../PA.Core/PA.Core.IF.Aspects"
import { IMetricParameterSet, IMetrics, IMetricsSet } from "../PA.Metric.Services/PA.Metrics.IF"
import {
    IPeriodMetricContext,
    IPeriodMetrics,
    IPeriod,
    IMetricCalculator,
    IMetricCalculatorCalculationChangedEvent
} from "./PA.UIM.Metric.IF";
import { MetricParameters, MetricsParameterSet } from "../PA.Metric.Services/PAMetrics"
import { ASync } from "../PA.Core/Basic/ASync";
import { Subscription, BehaviorSubject } from 'rxjs'
import { WB_CustomerRow, WB_FinishedProductRow } from "../PA.Core/PAdbSchemaRows";
import { IDateRange, IProgressProvider } from "../PA.Core/PA.Core.IF.Basic";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { CollectionUtils } from "../PA.Core/Basic/Collections"
import { PAMetricServiceClient } from "../PA.Metric.Services/PAMetricServiceClient";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { SourceCustomerService } from "../PA.MD.Services/SourceCustomerService";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { esWB_Customer } from "../PA.Core/PAdbSchema";
import { IFiscalCalendarPeriods, IRetailerPromotionCalendar } from "../PA.MD.Core/PA.MD.IF.Calendar"
import { CalendarUtils } from "../PA.MD.Services/CalendarUtils"
import { IVolumeAlignment, IVolumeFit, VolumeAlignmentUtils } from "../PA.TPM.Services/VolumeAlignmentUtils"

//
//  Primary output
//
//    Collection of Metrics over an array
//
//
//

interface PeriodMetricsCalcRequest {
    PeriodSet: { [id: string]: IPeriod },
    PeriodMetricsParameterSet: IMetricParameterSet,
    PeriodMetricSet?: IMetricsSet
}


export class PeriodMetricContext
    implements IPeriodMetricContext {

    _loadCount: number = 0
    _calcRequestCount: number = 0

    _bCalculationInProgress: boolean = false
    _calcProgressProvider: ProgressProvider = undefined

    _executionBounds: IHaveExecutionBounds = undefined
    _ebOnChangeSubscription: Subscription = undefined
    _bufferWeekCount: number = 2
    _executionBoundsChanged: BehaviorSubject<IPeriodMetricContext>;

    _planningCustomers_All: WB_CustomerRow[] = []
    _planningCustomers_Selected: WB_CustomerRow[] = []
    _sourceCustomers_All: WB_CustomerRow[] = []
    _sourceCustomers_Selected: WB_CustomerRow[] = []
    _finishedProducts_All: WB_FinishedProductRow[] = []
    _finishedProducts_Selected: WB_FinishedProductRow[] = []
    _selectedChanged: BehaviorSubject<IPeriodMetricContext>;

    _bCalculatingPeriodsChanged: BehaviorSubject<IPeriodMetricContext>;
    _onPeriodCalculationComplete: Promise<IPeriodMetricContext> = undefined
    _calculatedPeriodsChanged: BehaviorSubject<IPeriodMetricContext>;
    _calculatedPeriods: IPeriodMetrics[] = []

    _metricCalculator: IMetricCalculator = undefined
    _metricCalculatorSub: Subscription = undefined
    _bPassBoundsProductGroups: boolean = false

    _periodType = 'Observation'

    _bLoadingCalendarData: boolean
    _retailerCalendar: IRetailerPromotionCalendar
    _fiscalCalendarPeriods: IFiscalCalendarPeriods

    constructor(options?: { bPassExecutionBoundProductGroups?: boolean }) {
        const opts = options || {}
        this._calcProgressProvider = new ProgressProvider("Calculation Progress")
        this._calculatedPeriodsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._executionBoundsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._selectedChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        this._bCalculatingPeriodsChanged = new BehaviorSubject<IPeriodMetricContext>(this)
        if (_.isBoolean(opts.bPassExecutionBoundProductGroups)) {
            this._bPassBoundsProductGroups = opts.bPassExecutionBoundProductGroups
        }
        this._bLoadingCalendarData = false
        this._retailerCalendar = undefined
        this._fiscalCalendarPeriods = undefined
    }


    Register(anEB: IHaveExecutionBounds): void {
        const self = this

        if (_.isEmpty(anEB)) {
            this.UnRegister()
            return
        }

        this._loadCount += 1
        const internalLoadCount = this._loadCount

        let loadPromise
        if (anEB.bIHaveExecutionBoundsLoaded)
            loadPromise = anEB.OnIHaveExecutionBoundsLoaded
        else
            loadPromise = ASync.resolve(anEB)

        loadPromise
            .then((loadedEB: IHaveExecutionBounds) => {

                if (internalLoadCount != self._loadCount) {
                    // don't do anything
                    return;
                }
                self.UnRegister()

                // Setup the rest..
                self._executionBounds = loadedEB

                self._ebOnChangeSubscription
                    = loadedEB.ExecutionBoundsChanged.subscribe(self.onExecutionBoundsChanged.bind(self))


                self.queueCalc()
                return
            })
            .catch((err: any) => {
                if (internalLoadCount != self._loadCount) {
                    // don't do anything
                    return;
                }
                // Do I unregister ?
                self.UnRegister()
            })

    }

    UnRegister(): void {
        if (this._ebOnChangeSubscription) {
            this._ebOnChangeSubscription.unsubscribe()
        }
    }

    RegisterMetricCalculator(aCalculator: IMetricCalculator): void {
        const self = this

        if (_.isEmpty(aCalculator)) {
            this.UnRegisterMetricCalculator()
            return
        }

        self._metricCalculator = aCalculator
        self._metricCalculatorSub = self._metricCalculator.CalculationChanged.subscribe(self.onCalculationChanged.bind(self))

    }

    UnRegisterMetricCalculator(): void {
        if (this._metricCalculatorSub) {
            this._metricCalculatorSub.unsubscribe()
            this._metricCalculatorSub = undefined
        }

        this._metricCalculator = undefined
    }

    onCalculationChanged(event: IMetricCalculatorCalculationChangedEvent) {
        const self = this

        if (_.isEmpty(event)) {
            return
        }

        if (event.bRecalculateAll) {
            self.onExecutionBoundsChanged(self.ExecutionBounds)
        } else {

            self.updateCalculatedMetrics()
                .then((ignore) => {
                    self._calculatedPeriodsChanged.next(this)
                }).catch((err) => {
                console.log("onCalculationChanged: ", err)
            })
        }
    }

    updateCalculatedMetrics(): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            const aCalculator = self._metricCalculator

            if (_.isEmpty(aCalculator)) {
                resolve()
                return
            }

            async function CalculateAllMetrics() {
                const prds = self.CalculatedPeriods
                for (let i = 0; i < prds.length; i++) {
                    const prd = prds[i]
                    prd.CalcTotal = await aCalculator.CalculateMetrics(self, prd.Period, prd.Total)
                    prd.CalcInsideBounds = await aCalculator.CalculateMetrics(self, prd.Period, prd.InsideBounds)
                    prd.CalcOutsideBounds = await aCalculator.CalculateMetrics(self, prd.Period, prd.OutsideBounds)
                }
            }

            CalculateAllMetrics()
                .then((ignore) => {
                    resolve()
                }).catch((err) => {
                error(err)
            })

        })
    }

    onExecutionBoundsChanged(anEB: IHaveExecutionBounds) {
        const self = this

        if (_.isEmpty(anEB)) {
            return
        }

        if (!anEB.bIHaveExecutionBoundsLoaded)
            return

        let dbContext: PAdbContext = undefined
        if (_.isEmpty(anEB.Execution_PlanningCustomers)) {
            dbContext = new PAdbContext()
        } else {
            dbContext = <any>_.first(anEB.Execution_PlanningCustomers).dataStore()
        }

        // ReSetup drop down list...
        //- Planning Customer
        CollectionUtils.sync(self._planningCustomers_All, self._executionBounds.Execution_PlanningCustomers)
        CollectionUtils.interesect(self._planningCustomers_Selected, self._planningCustomers_All)

        //- Source Customer
        //- Add Logic to Handle the empty case
        let sourceCustomerLoadPromise = undefined
        if (_.isEmpty(self._executionBounds.Execution_SourceCustomers)) {
            const aSourceCustomerService = new SourceCustomerService();
            sourceCustomerLoadPromise = aSourceCustomerService.getSrcCustIdsbyParent(_.map(self._planningCustomers_All, (aCust) => {
                return aCust.cif.WB_CUST_ID
            }))
        } else {
            sourceCustomerLoadPromise = ASync.resolve([])
        }

        sourceCustomerLoadPromise
            .then((arrSourceCustomersIds: number[]) => {
                if (_.isEmpty(self._executionBounds.Execution_SourceCustomers)) {
                    return dbContext.findUsingCache(esWB_Customer, arrSourceCustomersIds)
                } else {
                    return ASync.resolve(self._executionBounds.Execution_SourceCustomers)
                }
            })
            .then((arrSourceCustomers: WB_CustomerRow[]) => {

                //- Source Customers
                CollectionUtils.sync(self._sourceCustomers_All, arrSourceCustomers)
                CollectionUtils.interesect(self._sourceCustomers_Selected, self._sourceCustomers_All)

                //- Finished Product
                CollectionUtils.sync(self._finishedProducts_All, self._executionBounds.Execution_FinishedProducts)
                CollectionUtils.interesect(self._finishedProducts_Selected, self._finishedProducts_All)
                self._executionBoundsChanged.next(self)
                self._selectedChanged.next(self)

                // Recalc
                self.queueCalc()

            })
            .catch((err: any) => {
                console.log(err)
            })
    }

    get CalcProgressProvider(): IProgressProvider {
        return this._calcProgressProvider
    }


    get Calendars(): { key: string, text: string, tooltip: string }[] {
        const self = this
        const rv: { key: string, text: string, tooltip: string }[] = []

        // Add "Observation" Data Provider Calendar
        const obsStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(self._retailerCalendar?.consumptionDataProvider?.cif?.WEEK_START_DAY_OF_WEEK, 1)
        if (self._retailerCalendar) {
            //let weekRange = CalendarUtils.getWeekRangeDesc(obsStartDOW)
            //rv.push({key: "Observation", text: `${self._retailerCalendar?.consumptionDataProvider?.cif?.SOURCE_NAME || 'Data Provider'} Calendar, ${weekRange}`, tooltip: `Week: ${weekRange}`})
        } else {
            const weekRange = CalendarUtils.getWeekRangeDesc(obsStartDOW)
            rv.push({ key: "Observation", text: `Data Provider Calendar, ${weekRange}`, tooltip: `Week: ${weekRange}` })
        }

        // Add "Retail" Promotion Calendar
        const retailerStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(self._retailerCalendar?.startDayOfWeek, 7)
        if (self._retailerCalendar) {
            const weekRange = CalendarUtils.getWeekRangeDesc(retailerStartDOW)
            rv.push({ key: 'Retail', text: `Retailer Calendar, ${weekRange}`, tooltip: `Week: ${weekRange}` })
        } else {
            //let weekRange = CalendarUtils.getWeekRangeDesc(retailerStartDOW)
            //rv.push({ key: 'Retail', text: `Retailer Calendar, ${weekRange}`, tooltip: `Default Week: ${weekRange}`})
        }

        if (self._fiscalCalendarPeriods) {
            const name = self._fiscalCalendarPeriods.name || 'Default'
            rv.push({ key: 'Fiscal', text: `Fiscal Calendar, ${name}`, tooltip: `Hierarchy: ${name}` })
        }

        rv.push({ key: 'Day', text: "Day", tooltip: "Daily" })
        if (_.filter(rv, r => r.key == self._periodType).length == 0)
            self._periodType = _.first(rv).key

        if (_.filter(rv, r => r.key == self._periodType).length == 0)
            self._periodType = _.first(rv).key
        return rv
    }

    get PeriodType(): string {
        return this._periodType
    }

    set PeriodType(value: string) {
        this._periodType = value
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get BufferWeekCount(): number {
        return this._bufferWeekCount
    }

    set BufferWeekCount(value: number) {
        this._bufferWeekCount = value
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get ExecutionBoundsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._executionBoundsChanged
    }

    get ExecutionBounds(): IHaveExecutionBounds {
        return this._executionBounds
    }

    get PlanningCustomers_All(): WB_CustomerRow[] {
        return this._planningCustomers_All
    }


    get PlanningCustomers_Selected(): WB_CustomerRow[] {
        return this._planningCustomers_Selected
    }

    set PlanningCustomers_Selected(value: WB_CustomerRow[]) {
        CollectionUtils.sync(this._planningCustomers_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get SourceCustomers_All(): WB_CustomerRow[] {
        return this._sourceCustomers_All
    }

    get SourceCustomers_Selected(): WB_CustomerRow[] {
        return this._sourceCustomers_Selected
    }

    set SourceCustomers_Selected(value: WB_CustomerRow[]) {
        CollectionUtils.sync(this._sourceCustomers_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get FinishedProducts_All(): WB_FinishedProductRow[] {
        return this._finishedProducts_All
    }

    get FinishedProducts_Selected(): WB_FinishedProductRow[] {
        return this._finishedProducts_Selected
    }

    set FinishedProducts_Selected(value: WB_FinishedProductRow[]) {
        CollectionUtils.sync(this._finishedProducts_Selected, value)
        this._selectedChanged.next(this)
        this.queueCalc()
    }

    get SelectedChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._selectedChanged
    }

    get bCalculatingPeriods(): boolean {
        return (this._bCalculationInProgress)
    }


    get bCalculatingPeriodsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._bCalculatingPeriodsChanged;
    }

    get OnPeriodCalculationComplete(): Promise<IPeriodMetricContext> {
        return this._onPeriodCalculationComplete
    }

    get CalculatedPeriodsChanged(): BehaviorSubject<IPeriodMetricContext> {
        return this._calculatedPeriodsChanged
    }

    get CalculatedPeriods(): IPeriodMetrics[] {
        return this._calculatedPeriods
    }

    private loadCalendarData(): void {
        const self = this

        if (self._bLoadingCalendarData)
            return

        const eb = self._executionBounds
        if (_.isEmpty(eb))
            return

        if (!eb?.bIHaveExecutionBoundsLoaded) {
            eb.OnIHaveExecutionBoundsLoaded.finally(() => self.onExecutionBoundsChanged(self.ExecutionBounds))
            return
        }

        let bLoadRetailerCalendar = false
        let bLoadFiscalCalendarWeeks = false

        // Check if Loaded data is still valid
        // Check Retailer Calendar Info
        const wbSalesOrgId = eb.SalesOrg?.cif?.WB_SALESORG_ID
        const wbCompanyId = eb.Company?.cif?.WB_COMPANY_ID || eb.SalesOrg?.cif?.WB_COMPANY_ID
        const customer = _.first(eb.Execution_PlanningCustomers)
        const wbCustId = customer?.cif?.WB_CUST_ID

        bLoadRetailerCalendar = !CalendarUtils.hasLoadedRetailCalendars(wbSalesOrgId, wbCustId)
        bLoadFiscalCalendarWeeks = !CalendarUtils.hasLoadedFiscalCalendarLevelNodes(wbCompanyId, "Week");

        // Notify for drop down changes
        self._bCalculatingPeriodsChanged.next(self)

        //--
        if (!(bLoadFiscalCalendarWeeks || bLoadRetailerCalendar)) {
            self._retailerCalendar = _.first(CalendarUtils.getRetailCalendars(wbSalesOrgId, [wbCustId], eb.Execution_InStore.StartDt, eb.Execution_InStore.EndDt))
            self._fiscalCalendarPeriods = CalendarUtils.getFiscalCalendarLevelNodes(wbCompanyId, "Week")
            return
        }

        self._bLoadingCalendarData = true;
        (async () => {
            try {
                //--
                if (bLoadRetailerCalendar) {
                    await CalendarUtils.loadRetailCalendars(wbSalesOrgId, [wbCustId])
                }

                if (bLoadFiscalCalendarWeeks) {
                    await CalendarUtils.loadFiscalCalendarLevelNodes(wbCompanyId, "Week")
                }

                self._retailerCalendar = _.first(CalendarUtils.getRetailCalendars(wbSalesOrgId, [wbCustId], eb.Execution_InStore.StartDt, eb.Execution_InStore.EndDt))
                self._fiscalCalendarPeriods = CalendarUtils.getFiscalCalendarLevelNodes(wbCompanyId, "Week")

                self._bLoadingCalendarData = false
                self.queueCalc()
            } catch (err) {
                self._bLoadingCalendarData = false
            }
        })();

    }

    private queueCalc(): void {
        const self = this

        self._calcRequestCount += 1

        self.loadCalendarData()

        const eb = self._executionBounds
        if (self.bCalculatingPeriods || _.isEmpty(eb)) {
            return;
        }
        if (!eb.bIHaveExecutionBoundsLoaded) {
            eb.OnIHaveExecutionBoundsLoaded.finally(() => {
                if (eb.bIHaveExecutionBoundsLoaded)
                    self.queueCalc()
            })
            return
        }

        self._bCalculationInProgress = true
        self._calcProgressProvider.start("Calculating Metrics")

        const recordedCalcRequestCount = this._calcRequestCount

        self._onPeriodCalculationComplete = new Promise<IPeriodMetricContext>((resolve, error) => {
            //console.log("Calculating Period Metrics: ", recordedCalcRequestCount)
            let apmps: any = undefined
            let aMetricServiceClient = undefined
            try {
                apmps = self.createPeriodMetricServiceParameterSet()
                aMetricServiceClient = new PAMetricServiceClient()
            } catch (err) {
                self._onPeriodCalculationComplete = undefined
                self._bCalculationInProgress = false
                self._calcProgressProvider.exception(err)
                error(err)
                return
            }

            if (_.isEmpty(apmps)) {
                self._onPeriodCalculationComplete = undefined
                self._bCalculationInProgress = false
                self._calcProgressProvider.finish()
                resolve(self)
                return
            }

            aMetricServiceClient.calculateMetricSet(apmps.PeriodMetricsParameterSet)
                .then((results: IMetricsSet) => {
                    apmps.PeriodMetricSet = results
                    self._calcProgressProvider.setPercentComplete(0.50)
                    return self.processMetricSet(apmps)
                })
                .then((ignore) => {
                    self._calcProgressProvider.setPercentComplete(0.90)
                    self._onPeriodCalculationComplete = undefined
                    self._bCalculationInProgress = false
                    self._calcProgressProvider.finish()
                    self._notifybPeriodCalculatingChanged()
                    // check if we need to recalc..
                    if (recordedCalcRequestCount < self._calcRequestCount) {
                        self.queueCalc()
                    }
                    resolve(self)
                })
                .catch((err: any) => {
                    self._onPeriodCalculationComplete = undefined
                    self._bCalculationInProgress = false
                    self._calcProgressProvider.exception(err)
                    self._notifybPeriodCalculatingChanged()
                    // check if we need to recalc..
                    if (recordedCalcRequestCount < self._calcRequestCount) {
                        self.queueCalc()
                    }
                    error(err)
                })
        })

        self._notifybPeriodCalculatingChanged()

    }

    private _notifybPeriodCalculatingChanged() {
        this._bCalculatingPeriodsChanged.next(this)
    }


    private createPeriodMetricServiceParameterSet(): PeriodMetricsCalcRequest {
        const self = this

        const eb = self._executionBounds
        if (_.isEmpty(eb)) {
            return undefined
        }
        const mps: MetricsParameterSet = new MetricsParameterSet()
        const periodSet: { [id: string]: IPeriod } = {}

        // Generate Periods..
        // Get outside bounds of shipments and consumption
        let OutsideBounds: IDateRange
        const ConsRange = {
            StartDt: eb.Execution_InStore_Aligned.StartDt,
            EndDt: eb.Execution_InStore_Aligned.EndDt
        }

        const ShipRange = {
            StartDt: eb.Execution_Shipment_Aligned.StartDt,
            EndDt: eb.Execution_Shipment_Aligned.EndDt
        }

        OutsideBounds = DateUtils.RangeUnion(ConsRange, ShipRange)

        if (_.isEmpty(OutsideBounds))
            return undefined

        const OutsideBoundsWithBuffer: IDateRange = {
            StartDt: DateUtils.addDays(OutsideBounds.StartDt, -7 * this.BufferWeekCount),
            EndDt: DateUtils.addDays(OutsideBounds.EndDt, 7 * this.BufferWeekCount)
        }

        if (!_.isDate(OutsideBoundsWithBuffer.StartDt) || !_.isDate(OutsideBoundsWithBuffer.EndDt)) {
            return undefined
        }


        function setupPeriodMetricParameters(ip: IPeriod) {
            const periodRange = ip.Range

            const cnsOffset = eb.VolumeAlignment?.ConsumptionDataRetailerDateOffset || 0

            const mp = new MetricParameters()
            ip.Params = mp

            mp.MetricParameterId = ip.Id
            mp.bCalculateIntersection = true
            if (self.PlanningCustomers_Selected.length > 0) {
                mp.PlanningCustomers = _.map(self.PlanningCustomers_Selected, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            } else {
                mp.PlanningCustomers = _.map(eb.Execution_PlanningCustomers, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            }
            if (self.SourceCustomers_Selected.length > 0) {
                mp.SourceCustomers = _.map(self.SourceCustomers_Selected, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            } else {
                mp.SourceCustomers = _.map(eb.Execution_SourceCustomers, (pcr: WB_CustomerRow) => {
                    return pcr.cif.WB_CUST_ID
                })
            }
            if (self.FinishedProducts_Selected.length > 0) {
                mp.FinishedProducts = _.map(self.FinishedProducts_Selected, (fpr: WB_FinishedProductRow) => {
                    return fpr.cif.WB_PROD_ID
                })
            } else {
                mp.FinishedProducts = _.map(eb.Execution_FinishedProducts, (fpr: WB_FinishedProductRow) => {
                    return fpr.cif.WB_PROD_ID
                })

                // If there are no products available, add non value.. to prevent full dataset.
                if (_.isEmpty(mp.FinishedProducts)) {
                    mp.FinishedProducts.push(-9999)
                }
            }

            if (self._bPassBoundsProductGroups) {
                mp.ProductGroups = _.map(eb.Execution_ProductGroups, (a) => a.cif.WB_PRDGRP_ID)
            }

            mp.ConsumerProductUPCs = []
            mp.Sources = []
            if (ip.bIgnoreCnsOffset) {
                // The service will adjust for the cns offset, undo the adjustment here.
                mp.InStoreStartDt = DateUtils.addDays(periodRange.StartDt, cnsOffset)
                mp.InStoreEndDt = DateUtils.addDays(periodRange.EndDt, cnsOffset)
            } else {
                mp.InStoreStartDt = periodRange.StartDt, cnsOffset
                mp.InStoreEndDt = periodRange.EndDt, cnsOffset
            }
            mp.ShipmentStartDt = periodRange.StartDt
            mp.ShipmentEndDt = periodRange.EndDt
            mp.Int_InStoreStartDt = eb.Execution_InStore_Aligned.StartDt
            mp.Int_InStoreEndDt = eb.Execution_InStore_Aligned.EndDt
            mp.Int_ShipmentStartDt = eb.Execution_Shipment_Aligned.StartDt
            mp.Int_ShipmentEndDt = eb.Execution_Shipment_Aligned.EndDt

            function cloneAndConvertToBumpEquivalent(src: IVolumeAlignment, checkExtend: boolean): IVolumeAlignment {
                // This is the default in MetricServiceHANA
                const dflt: IVolumeFit = { Process: "Simple", AssignmentPcnt: "EndingDate" }
                const rv: IVolumeAlignment = VolumeAlignmentUtils.defaultVolumeAlignmentSetKey(src, dflt)

                function convertVolumeFit(fit: IVolumeFit): IVolumeFit {
                    // To prevent adjacent periods from duplicating volume,
                    //    change policy to Ending Date and Extend overlapping period to full week
                    //
                    const rvf = VolumeAlignmentUtils.defaultVolumeFitSetKey(fit, dflt)
                    if (rvf.AssignmentPcnt == "100") {
                        rvf.AssignmentPcnt = "EndingDate"

                        if (checkExtend && (self._retailerCalendar?.consumptionData?.dataLevel != 'Day'))
                            rvf.ExtendToFullWeek = true
                    }

                    // Reset the Hash Key due to changes
                    VolumeAlignmentUtils.setKey(rvf)
                    return rvf
                }

                rv.Base = convertVolumeFit(rv.Base)
                rv.EDLP = convertVolumeFit(rv.EDLP)
                rv.Merch = convertVolumeFit(rv.Merch)
                rv.DispShipper = convertVolumeFit(rv.DispShipper)

                return rv
            }

            mp.Int_VolumeAlignment = cloneAndConvertToBumpEquivalent(eb.VolumeAlignment, true)
            mp.VolumeAlignment = cloneAndConvertToBumpEquivalent(eb.VolumeAlignment, false)
            mps[mp.MetricParameterId] = mp
        }

        function setupFixedPeriodCalendar(startDOW: number, periodLength: 1 | 7, bIgnoreConsumptionOffset: boolean): void {
            const boundsStartDOW = OutsideBoundsWithBuffer.StartDt.getDay() + 1
            let daysToExtendStart = boundsStartDOW - startDOW
            if (daysToExtendStart < 0)
                daysToExtendStart = 7 - (-daysToExtendStart)
            OutsideBoundsWithBuffer.StartDt = DateUtils.addDays(OutsideBoundsWithBuffer.StartDt, -1 * daysToExtendStart)

            const periodCount = Math.ceil(DateUtils.durationInDays(OutsideBoundsWithBuffer) / periodLength)
            let i
            for (i = 0; i < periodCount; i++) {
                //
                const fixedRange = {
                    StartDt: DateUtils.addDays(OutsideBoundsWithBuffer.StartDt,
                        i * periodLength),
                    EndDt: DateUtils.addDays(OutsideBoundsWithBuffer.StartDt,
                        ((i + 1) * periodLength) - 1)
                }
                const iPeriod: IPeriod = {
                    Id: i + '',
                    Name: self.getRangeDescription(fixedRange),
                    bIgnoreCnsOffset: bIgnoreConsumptionOffset,
                    Range: fixedRange
                }
                setupPeriodMetricParameters(iPeriod)

                periodSet[i] = iPeriod

            }
        }

        function setupFiscalPeriodSet(): void {
            //
            let periods = self._fiscalCalendarPeriods?.periods || []
            const boundsStart = OutsideBoundsWithBuffer.StartDt
            const boundsEnd = OutsideBoundsWithBuffer.EndDt
            periods = _.filter(periods, (p) => DateUtils.RangeDatesOverlap(p.periodStartDt, p.periodEndDt, boundsStart, boundsEnd))
            if (_.isEmpty(periods)) {
                const obsStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(self._retailerCalendar.consumptionDataProvider?.cif?.WEEK_START_DAY_OF_WEEK, 1)
                setupFixedPeriodCalendar(obsStartDOW, 7, false)
                return
            }

            let i
            for (i = 0; i < periods.length; i++) {
                //
                const fPeriod = periods[i]

                const fiscalRange = {
                    StartDt: fPeriod.periodStartDt,
                    EndDt: fPeriod.periodEndDt
                }
                const iPeriod: IPeriod = {
                    Id: i + '',
                    Name: self.getRangeDescription(fiscalRange),  // fPeriod.periodId ||
                    Range: fiscalRange
                }

                setupPeriodMetricParameters(iPeriod)

                periodSet[i] = iPeriod
            }
        }

        switch (self._periodType) {
            case "Day":
                setupFixedPeriodCalendar(OutsideBoundsWithBuffer.StartDt.getDay() + 1, 1, false)
                break
            case "Observation":
                const obsStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(self._retailerCalendar?.consumptionDataProvider?.cif?.WEEK_START_DAY_OF_WEEK, 1) // Sunday
                setupFixedPeriodCalendar(obsStartDOW, 7, true)
                break
            case "Retail":
                const retailerStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(self._retailerCalendar?.startDayOfWeek, 7) // Saturday
                const cnsOffset = self._retailerCalendar?.consumptionData?.dataOffset || 0
                setupFixedPeriodCalendar(retailerStartDOW, 7, false)
                break
            case "Fiscal":
                setupFiscalPeriodSet()
                break
            default:
                console.log(`Unknown Period Type: ${self._periodType}`)
        }


        return { PeriodSet: periodSet, PeriodMetricsParameterSet: mps }
    }

    private getRangeDescription(aRange: IDateRange): string {

        const s_dd = aRange.StartDt.getDate()
        const s_mm = (aRange.StartDt.getMonth() + 1)
        const s_yyyy = aRange.StartDt.getFullYear()

        const e_dd = aRange.EndDt.getDate()
        const e_mm = (aRange.EndDt.getMonth() + 1)
        const e_yyyy = aRange.EndDt.getFullYear()

        if (s_yyyy == e_yyyy)
            return e_mm + '/' + e_dd + ''
        else
            return e_mm + '/' + e_dd + '/' + (e_yyyy % 100) + ''
    }

    private processMetricSet(p: PeriodMetricsCalcRequest): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {

            // Put results into Period Calc Records..
            if (_.isEmpty(p.PeriodMetricSet)) {
                resolve()
                return
            }

            const periodsSorted: IPeriod[] = _.sortBy(_.values(p.PeriodSet), (aPeriod: IPeriod) => {
                return aPeriod.Range.StartDt
            })

            self.CalculatedPeriods.length = 0

            _.forEach(periodsSorted, (aPeriod: IPeriod) => {
                const metrics = p.PeriodMetricSet[aPeriod.Id]
                if (!metrics) {
                    return // missing metrics for period
                }
                const outsideBoundMetrics: IMetrics = metrics.clone()
                outsideBoundMetrics.subtract(metrics.IntersectionMetrics)
                const periodMetrics: IPeriodMetrics =
                    {
                        Period: aPeriod,
                        Total: metrics,
                        InsideBounds: metrics.IntersectionMetrics,
                        OutsideBounds: outsideBoundMetrics
                    }

                self.CalculatedPeriods.push(periodMetrics)
            })

            const aCalculator = self._metricCalculator
            let beforeCall
            if (_.isEmpty(aCalculator)) {
                beforeCall = ASync.resolve()
            } else {
                beforeCall = aCalculator.BeforeCalcualteMetrics(self, p.PeriodMetricsParameterSet)
            }

            beforeCall
                .then((ignore) => {
                    return self.updateCalculatedMetrics()
                })
                .then((ignore) => {
                    self._calculatedPeriodsChanged.next(self)
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })

    }
}